import { Autocomplete, TextField } from "@mui/material";
import { useMemo } from "react";
import { useController, useFormContext } from "react-hook-form";
import FormField from "./FormField";

export default function MuiSelectField({
  name,
  label,
  placeholder,
  options,
  isClearable = true,
  disabled = false,
  required = false,
  multiple = false,
  loading = false,
  displayEmpty = false,
  emptyLabel = "Selecione",
  hint,
  labelSx,
  sx,
  slotProps = {},
  ...rest
}) {
  const { control } = useFormContext();

  const rules = useMemo(() => {
    if (!required) return undefined;

    if (multiple) {
      return {
        validate: (value) =>
          (Array.isArray(value) && value.length > 0) ||
          "Selecione pelo menos uma opção",
      };
    }

    // ✅ PATCH: required que aceita false/0 e só falha em null/undefined/""
    return {
      validate: (value) =>
        value === null || value === undefined || value === ""
          ? "Campo obrigatório"
          : true,
    };
  }, [required, multiple]);

  const {
    field: { ref, onChange, value, ...field },
    fieldState: { error },
  } = useController({ name, control, rules });

  const getValue = useMemo(() => {
    if (multiple) {
      return options.filter((opt) => (value || []).includes(opt.value));
    } else {
      const found = options.find((opt) => opt.value === value);
      return found || (displayEmpty ? { label: emptyLabel, value: "" } : null);
    }
  }, [options, value, multiple, displayEmpty, emptyLabel]);

  const handleChange = (_, newValue) => {
    if (multiple) {
      onChange(newValue.map((item) => item.value));
    } else {
      onChange(newValue && newValue.value !== "" ? newValue.value : null);
    }
  };

  return (
    <FormField
      label={label}
      hint={hint}
      error={error?.message}
      disabled={disabled}
      required={required}
      labelSx={labelSx}
    >
      <Autocomplete
        {...field}
        sx={{
          ".MuiInputBase-root": {
            padding: "1px 9px",
            boxShadow: "0px 1px 1px 0px rgba(0,0,0,0.2)",
          },
          width: "100%",
          ...sx,
        }}
        multiple={multiple}
        options={options}
        disableClearable={!isClearable}
        value={getValue}
        onChange={handleChange}
        disabled={disabled}
        loading={loading}
        isOptionEqualToValue={(option, val) => option.value === val.value}
        getOptionLabel={(option) => option.label}
        renderInput={(params) => (
          <TextField
            {...params}
            inputRef={ref}
            placeholder={!value && displayEmpty ? emptyLabel : placeholder}
            error={!!error}
            required={!multiple && required}
            slotProps={slotProps}
          />
        )}
        {...rest}
      />
    </FormField>
  );
}
